import React from 'react';
import PropTypes from 'prop-types';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import PersonOutlinedIcon from '@material-ui/icons/PersonOutlined';
import AddIcon from '@material-ui/icons/Add';

import { getString } from '../../../../helpers/stringHelpers.js';
import ConsentedPersonsCard from './ConsentedPersonsCard/ConsentedPersonsCard';
import IndentedCardHeader from '../../../../components/IndentedCardHeader/IndentedCardHeader.js';
import IndentedCardContent from '../../../../components/IndentedCardContent/IndentedCardContent.js';
import ConsentedPersonsDialog from './ConsentedPersonsDialog/ConsentedPersonsDialog';
import ConfirmedDialog from '../../../../components/ConfirmedDialog/ConfirmedDialog';
import ConfirmConsentDialog from './ConfirmConsentDialog/ConfirmConsentDialog';
import TooManyConsentsDialog from './TooManyConsentsDialog/TooManyConsentsDialog';
import DeleteDialog from '../../../../components/DeleteDialog/DeleteDialog';
import FabWithText from '../../../../components/FabWithText/FabWithText';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

const stringsComponent = 'consentedPersons';

const MAXIMUM_ACT_BEHALF = 9;

class ConsentedPersonsSection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      deleteDialogOpen: false,
      confirmDialogOpen: false,
      tooManyDialogOpen: false,
      contactToDelete: null,
      dialogData: null,
    };

    this.handleConfirmed = this.handleConfirmed.bind(this);
    this.toggleCreateDialog = this.toggleCreateDialog.bind(this);
    this.handleCreateDialogSubmit = this.handleCreateDialogSubmit.bind(this);
    this.handleConfirmDialogSubmit = this.handleConfirmDialogSubmit.bind(this);
    this.removePerson = this.removePerson.bind(this);
    this.handleDeleteDialogSubmit = this.handleDeleteDialogSubmit.bind(this);
    this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
    this.toggleConfirmDialog = this.toggleConfirmDialog.bind(this);
    this.toggleTooManyDialog = this.toggleTooManyDialog.bind(this);
    this.maximumConsentsReached = this.maximumConsentsReached.bind(this);
  }

  toggleConfirmDialog() {
    this.setState({
      confirmDialogOpen: !this.state.confirmDialogOpen,
    });
  }

  toggleTooManyDialog() {
    this.setState({
      tooManyDialogOpen: !this.state.tooManyDialogOpen,
    });
  }

  handleConfirmed() {
    const { setConfirmedOpen } = this.props;
    setConfirmedOpen();
  }

  toggleCreateDialog() {
    this.setState({
      createDialogOpen: !this.state.createDialogOpen,
    });
  }

  toggleDeleteDialog() {
    this.setState({
      deleteDialogOpen: !this.state.deleteDialogOpen,
    });
  }

  handleCreateDialogSubmit(dialogData) {
    this.setState({ dialogData: dialogData });

    this.toggleCreateDialog();
    this.toggleConfirmDialog();
  }

  handleConfirmDialogSubmit() {
    const { createConsentedPerson } = this.props;

    const { name, relationship, address, phone, dateOfBirth } =
      this.state.dialogData;

    const details = `${name} | ${relationship} | ${address} | ${phone} | DOB ${dateOfBirth}`;
    const data = {
      details: details,
      source: 'ONLINE',
    };

    createConsentedPerson(data);

    this.toggleConfirmDialog();
  }

  removePerson(index) {
    const { consentedPersonsData } = this.props;
    this.setState({ contactToDelete: consentedPersonsData[index] });
    this.toggleDeleteDialog();
  }

  handleDeleteDialogSubmit() {
    const { deleteConsentedPerson } = this.props;
    const { contactToDelete } = this.state;
    deleteConsentedPerson(contactToDelete.id);
  }

  maximumConsentsReached() {
    const { consentedPersonsData } = this.props;
    if (consentedPersonsData) {
      const actOnBehalfCount = consentedPersonsData.filter(
        (consentedPerson) =>
          !consentedPerson.type || consentedPerson.type.includes('ACTBEHALF')
      ).length;
      if (actOnBehalfCount >= MAXIMUM_ACT_BEHALF) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  render() {
    const { classes, confirmedOpen, consentedPersonsData, canEdit } =
      this.props;

    return (
      <div className={classes.root}>
        <Card>
          <IndentedCardContent
            body={
              <div className={classes.bodyContainer}>
                {consentedPersonsData &&
                  consentedPersonsData.map((person, index) => {
                    let details = person.details;
                    if (!details) {
                      if (person.type === 'PIVWAVE') {
                        details = getString(
                          stringsComponent,
                          'PIVWAVE_DEFAULT_TEXT'
                        );
                      } else {
                        details = getString(
                          stringsComponent,
                          'ACTBEHALF_DEFAULT_TEXT'
                        );
                      }
                    }

                    return (
                      <ConsentedPersonsCard
                        details={details}
                        removeAction={() => this.removePerson(index)}
                        showUpdating={person.updating}
                        key={`consented-person-${index}`}
                        canEdit={canEdit}
                      />
                    );
                  })}
                {canEdit && (
                  <div className={classes.actionContainer}>
                    <FabWithText
                      label={getString(stringsComponent, 'LABEL_FAB_ADD')}
                      icon={AddIcon}
                      action={() => {
                        if (this.maximumConsentsReached()) {
                          this.toggleTooManyDialog();
                        } else {
                          this.toggleCreateDialog();
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            }
            noIndent={true}
          />
        </Card>
        {this.state.createDialogOpen && (
          <ConsentedPersonsDialog
            open={this.state.createDialogOpen}
            handleCancel={() => this.toggleCreateDialog()}
            handleSubmit={(dialogData) =>
              this.handleCreateDialogSubmit(dialogData)
            }
          />
        )}
        {confirmedOpen && (
          <ConfirmedDialog
            title={getString(stringsComponent, 'MY_DETAILS_TITLE')}
            open={confirmedOpen}
            close={this.handleConfirmed}
          />
        )}
        {this.state.deleteDialogOpen && (
          <DeleteDialog
            open={this.state.deleteDialogOpen}
            cancel={() => this.toggleDeleteDialog()}
            deleteContact={this.handleDeleteDialogSubmit}
            dialogContentNoSubject={getString(
              stringsComponent,
              'DELETE_CONSENTED_MESSAGE'
            )}
          />
        )}
        {this.state.confirmDialogOpen && (
          <ConfirmConsentDialog
            open={this.state.confirmDialogOpen}
            cancel={() => this.toggleConfirmDialog()}
            confirm={() => this.handleConfirmDialogSubmit()}
            name={this.state.dialogData.name}
            relationship={this.state.dialogData.relationship}
            address={this.state.dialogData.address}
            phone={this.state.dialogData.phone}
            dateOfBirth={this.state.dialogData.dateOfBirth}
          />
        )}
        {this.state.tooManyDialogOpen && (
          <TooManyConsentsDialog
            open={this.state.tooManyDialogOpen}
            confirm={() => this.toggleTooManyDialog()}
          />
        )}
      </div>
    );
  }
}

ConsentedPersonsSection.propTypes = {
  classes: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default withStyles(styles, { withTheme: true })(ConsentedPersonsSection);
